import React from 'react';
import { cn } from '@/lib/utils';
import { CircularProgressProps } from './types';

const DEFAULT_MAX_VALUE = 100;
const DEFAULT_PROGRESS_SIZE = 80;
const DEFAULT_STROKE_WIDTH = 8;
const HALF_DIVISOR = 2;
const CIRCLE_MULTIPLIER = 2;

const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  maxValue = DEFAULT_MAX_VALUE,
  size = DEFAULT_PROGRESS_SIZE,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  children,
  className = '',
}) => {
  if (typeof value !== 'number' || value < 0) {
    console.warn('CircularProgress: value must be a non-negative number');
    return null;
  }

  if (maxValue <= 0) {
    console.warn('CircularProgress: maxValue must be greater than 0');
    return null;
  }

  const radius = (size - strokeWidth) / HALF_DIVISOR;
  const circumference = radius * CIRCLE_MULTIPLIER * Math.PI;
  const progress = Math.min((value / maxValue) * 100, 100); // Clamp to 100%
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const centerPosition = size / HALF_DIVISOR;

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={maxValue}
      aria-label={`Progress: ${value} of ${maxValue}`}
    >
      <svg width={size} height={size} className="transform -rotate-90" aria-hidden="true">
        <circle
          cx={centerPosition}
          cy={centerPosition}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={centerPosition}
          cy={centerPosition}
          r={radius}
          stroke="#38bdf8"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-300 ease-in-out"
          strokeLinecap="round"
        />
      </svg>
      {children && (
        <div className="absolute flex items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );
};

CircularProgress.displayName = 'CircularProgress';

export { CircularProgress };
